import type { DigestInfo, DigestSectionKey } from '@brimstone/shared';
import { Link, useParams } from 'react-router';
import { PhotosPanel } from '../../components/PhotosPanel';
import { EmptyState, PageLayout, Panel, SecondaryButton } from '../../components/ui';
import { useDigest, useDigests, useRegenerateDigest } from '../../lib/digest';
import { formatDate } from '../../lib/time';

const TITLES: Record<DigestSectionKey, string> = {
  headline: 'The week',
  levels: 'Levels',
  legacy: 'Legacy',
  discoveries: 'Discoveries',
  rares: 'Rares',
  deaths: 'Deaths',
  books: 'Books',
  loot: 'Loot',
  networth: 'Net worth',
  quiet: 'Quiet',
  lookahead: 'Next week',
};

/** "18 Nov – 25 Nov 2026", reset to reset. */
function weekLabel(d: Pick<DigestInfo, 'startAt' | 'endAt'>): string {
  const day = new Date(d.startAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return `${day} – ${formatDate(d.endAt)}`;
}

/** /digest (SPEC §6.11): the archive, newest week first. */
export function DigestListPage() {
  const q = useDigests();
  const list = q.data?.digests;
  return (
    <PageLayout
      main={
        <Panel title="Weekly digest" meta={list ? `${list.length} WEEKS` : undefined}>
          {!list ? (
            <EmptyState>{q.isError ? 'Couldn’t load the digests.' : 'Loading…'}</EmptyState>
          ) : list.length === 0 ? (
            <EmptyState>
              No digests yet — one is written 15 minutes after each weekly reset.
            </EmptyState>
          ) : (
            <ul className="m-0 list-none p-0">
              {list.map((d) => (
                <li key={d.weekId} className="border-b border-line-soft last:border-b-0">
                  <Link
                    to={`/digest/${d.weekId}`}
                    className="flex min-h-11 flex-col gap-1 px-4 py-3 text-text no-underline hover:bg-line-soft"
                  >
                    <span className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-mono text-secondary text-muted">{weekLabel(d)}</span>
                      {d.partial && (
                        <span className="font-mono text-label text-muted">IN PROGRESS</span>
                      )}
                    </span>
                    <span>{d.headline}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      }
    />
  );
}

/** /digest/:weekId: the week's digest, each section linking to its detail. */
export function DigestPage() {
  const { weekId = '' } = useParams();
  const q = useDigest(weekId);
  const regenerate = useRegenerateDigest();
  const all = useDigests().data?.digests ?? [];
  const index = all.findIndex((d) => d.weekId === weekId);
  const newer = index > 0 ? all[index - 1] : undefined;
  const older = index >= 0 ? all[index + 1] : undefined;
  const d = q.data?.digest;
  return (
    <PageLayout
      main={
        <Panel
          title={d ? `Week of ${weekLabel(d)}` : 'Weekly digest'}
          meta={d?.partial ? 'IN PROGRESS' : undefined}
        >
          {!d ? (
            <EmptyState>
              {q.isError ? (
                <>
                  No digest for that week. <Link to="/digest">All weeks</Link>
                </>
              ) : (
                'Loading…'
              )}
            </EmptyState>
          ) : (
            <>
              <dl className="m-0 flex flex-col">
                {d.sections.map((s) => (
                  <div
                    key={s.key}
                    className="grid gap-x-4 gap-y-1 border-b border-line-soft px-4 py-3 sm:grid-cols-[120px_minmax(0,1fr)]"
                  >
                    <dt className="text-label uppercase tracking-label text-muted">
                      {TITLES[s.key]}
                    </dt>
                    <dd className="m-0">
                      {s.text}
                      {s.link && (
                        <>
                          {' '}
                          <Link to={s.link} className="whitespace-nowrap text-compact">
                            Details →
                          </Link>
                        </>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <span className="flex gap-4 text-compact">
                  {older && <Link to={`/digest/${older.weekId}`}>← Older</Link>}
                  {newer && <Link to={`/digest/${newer.weekId}`}>Newer →</Link>}
                  <Link to="/digest">All weeks</Link>
                </span>
                <SecondaryButton
                  type="button"
                  disabled={regenerate.isPending}
                  onClick={() => regenerate.mutate(d.weekId)}
                >
                  {regenerate.isPending ? 'Writing…' : 'Regenerate'}
                </SecondaryButton>
              </div>
              <p className="m-0 px-4 pb-3 font-mono text-label text-muted">
                Written {formatDate(d.generatedAt)}
              </p>
            </>
          )}
        </Panel>
      }
      side={
        d && (
          <PhotosPanel
            filters={{ characterIds: [], week: d.weekId }}
            limit={6}
            level="side"
            empty="No photos this week."
          />
        )
      }
    />
  );
}
